// Fichier : EditUserTabs.tsx
// Ce fichier contient la définition et l'implémentation du composant React pour modifier les onglets d'un utilisateur existant.
// Il utilise React Hook Form et Zod pour la validation de formulaire, RTK Query Pour la gestion des mutations, et React Roastify pour Notifications .

import { useState, useEffect } from "react";
import {
  useGetTabsQuery,
  useGetUserTabsQuery,
  useEditUserTabsMutation,
} from "@/state/usersApiSlice";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./EditUserTabs.module.css";
import { toast } from "react-toastify";
import { Checkbox } from "@/components/ui/checkbox";

const EditUserTabs = () => {
  // Fonction de notification pour afficher un message de succès
  const notifyUpdateUserTabsSuccess = () =>
    toast.success("User Tabs Updated Successfully", {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });

  const navigate = useNavigate(); // Hook pour la navigation
  const { id } = useParams(); // Hook pour récupérer les paramètres de l'URL (ID de l'utilisateur)
  const { data, isLoading, isError } = useGetTabsQuery(); // Requête pour obtenir la liste des Tabs.
  const {
    data: userTabs,
    isLoading: userLoading,
    isError: userError,
  } = useGetUserTabsQuery(id); // Requête pour obtenir les onglets de l'utilisateur (basé sur l'ID de l'utilisateur)

  // État pour suivre les onglets cochés ou décochés
  const [checkedTabs, setCheckedTabs] = useState<number[]>([]);
  // Mutation pour mettre à jour les Tabs de l'utilisateur
  const [editUserTabs, { isSuccess, isError: updateError }] =
    useEditUserTabsMutation();

  useEffect(() => {
    // useEffect pour initialiser les onglets cochés basés sur les Tabs de l'utilisateur
    if (userTabs) {
      const initialCheckedTabs = userTabs.tabs.map(
        (userTab: any) => userTab.tabId
      );
      setCheckedTabs(initialCheckedTabs);
    }
  }, [userTabs]); // Avec la dependance "userTabs" , cette useEffect va se déclencher et modifier "CheckedTabs" par les Tabs actives pour l'utilisateur donné.

  // Fonction pour gérer le changement d'état des cases à cocher
  const handleCheckboxChange = (tabId: number) => {
    setCheckedTabs((prevCheckedTabs) =>
      prevCheckedTabs.includes(tabId)
        ? prevCheckedTabs.filter((id) => id !== tabId)
        : [...prevCheckedTabs, tabId]
    );
  }; // une fois un checkbox est cochée ou décochée , on met a jour "CheckedTabs" , soit on ajoute une Tab sur la liste , soit on l'enléve .

  const handleSelectAllButton = () => {
    const tabIds = data.map((tab: any) => tab.id);
    setCheckedTabs(tabIds);
  };

  const handleUnselectAllButton = () => {
    setCheckedTabs([]);
  };

  // Fonction pour gérer la soumission du formulaire
  const handleSubmit = async () => {
    const formattedData = checkedTabs.map((tabId) => ({
      tabId: tabId.toString(), // Conversion du tabId en chaîne de caractères pour le backend
    }));
    try {
      await editUserTabs({ id, tabs: formattedData }).unwrap(); // Si la mise à jour réussit, afficher une notification et rediriger
      notifyUpdateUserTabsSuccess();
      navigate(`/users/${id}/tabs/view`);
    } catch (error) {
      console.error("Failed to update user tabs", error);
    }
  };

  // Affichage conditionnel en fonction de l'état de la requête
  if (isLoading || userLoading) {
    return <div>Loading...</div>; // Afficher un message de chargement
  }

  if (isError || userError) {
    return <div>Error loading data.</div>; // Afficher un message d'erreur
  }

  return (
    <div className={styles.container}>
      <h3
        className="underline cursor-pointer mb-8 text-gray-500 text-lg"
        onClick={() => navigate("/users/all")}
      >
        Back to Users List
      </h3>
      <div className={styles.buttonsContainer}>
        {" "}
        <button
          className={styles.selectAllButton}
          onClick={handleSelectAllButton}
        >
          Select All
        </button>
        <button
          className={styles.unSelectAllButton}
          onClick={handleUnselectAllButton}
        >
          Unselect All
        </button>
      </div>
      <div className={styles["grid-container"]}>
        {/* Itération sur les Tabs pour afficher les cases à cocher */}
        {data.map((tab: any) => (
          <div className={styles["form-container"]} key={tab.id}>
            <Checkbox
              id={tab.id}
              name={tab.tab_name}
              checked={checkedTabs.includes(tab.id)}
              onCheckedChange={() => handleCheckboxChange(tab.id)}
            />
            <label htmlFor={tab.id}>{tab.tab_name}</label>
          </div>
        ))}

        {isSuccess && (
          <p className={styles["success-message"]}>
            Changes Saved Successfully
          </p>
        )}
        {updateError && (
          <p className={styles["error-message"]}>
            There is an Error, check the console
          </p>
        )}
      </div>
      <div className={styles.buttonsContainer}>
        <button className={styles.button} onClick={handleSubmit}>
          Save Changes
        </button>
        <button
          className={styles.unSelectAllButton}
          onClick={() => navigate(`/users/${id}/tabs/view`)}
        >
          Discard Changes
        </button>
      </div>
    </div>
  );
};

export default EditUserTabs;
